import { Events } from './events.enum';

// The event bus makes no delivery guarantees: `publish` is a no-op when nobody is subscribed, and a
// handler that throws never gets as far as publishing its reply. Awaiting a reply therefore has to
// be given up on at some point, otherwise a caller waits - and its button spins - forever.
const DEFAULT_TIMEOUT_MS = 30_000;

export class EventBusTimeoutError extends Error {
    constructor(event: Events) {
        super(`Timed out waiting for "${event}" on the event bus`);
        this.name = 'EventBusTimeoutError';
    }
}

type Options = { timeout?: number };

// Resolves with the value carried by the next `event`, or rejects once `timeout` has passed.
export const waitForEvent = <T = unknown>(event: Events, { timeout = DEFAULT_TIMEOUT_MS }: Options = {}) =>
    new Promise<T>((resolve, reject) => {
        let timeoutId: ReturnType<typeof setTimeout>;

        const subscriberId = window.EventBus.subscribe(event, (value: T) => {
            clearTimeout(timeoutId);
            window.EventBus.unsubscribe(subscriberId);
            resolve(value);
        });

        timeoutId = setTimeout(() => {
            window.EventBus.unsubscribe(subscriberId);
            reject(new EventBusTimeoutError(event));
        }, timeout);
    });

// Publishes `request` and waits for the matching `response`. Subscribing before publishing keeps a
// handler that replies synchronously from answering before anyone is listening.
export const requestOverEventBus = <T = unknown>(
    request: Events,
    response: Events,
    payload?: unknown,
    options?: Options
): Promise<T> => {
    const reply = waitForEvent<T>(response, options);
    window.EventBus.publish(request, payload);
    return reply;
};
